import { css } from '@emotion/react';
import {
  faCircleInfo, faWarning,
} from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ButtonComponent } from '~/_shared/baseComponents/buttons';
import { ButtonLinkComponent } from '~/_shared/baseComponents/links/button/buttonLink.component';
import type { TopUnderbarItem } from '~/_shared/components/topUnderbar/item/topUnderbarItem.types';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { limitNotificationsRemoveItem } from '~/store/frontendState/limitNotifications/limitNotifications.actionCreators';
import { useLimitNotificationsItemSelector } from '~/store/frontendState/limitNotifications/limitNotifications.selectors';

const notificationContentStyle = css({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  gap: 10,
  justifyContent: 'space-between',
  flexDirection: 'column',
  '@media (min-width: 500px)': {
    flexDirection: 'row',
  },
});

const notificationDescriptionStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const notificationTextStyle = css({
  fontSize: 14,
  whiteSpace: 'pre-line',
});

export const useLimitNotifications = (): TopUnderbarItem[] => {
  const notifications = Array.from(useLimitNotificationsItemSelector().values());
  const dispatch = useDispatch();
  const theme = useTheme();

  return useMemo(() => notifications.map<TopUnderbarItem>(item => {
    const colors = item.type === 'warning' ? {
      backgroundColor: theme.alertColors.warningSecondary,
      borderColor: theme.borderColors.error,
      iconColor: theme.textColors.warning,
      color: theme.textColors.warning,
    } : {
      backgroundColor: theme.alertColors.infoTertiary,
      borderColor: theme.alertColors.infoPrimary,
      iconColor: theme.textColors.link,
      color: theme.textColors.link,
    };

    return {
      key: item.key,
      icon: item.type === 'warning' ? faWarning : faCircleInfo,
      colors,
      padding: '10px 16px 10px 6px',
      children: (
        <div css={notificationContentStyle}>
          <div css={notificationDescriptionStyle}>
            <div><strong>{item.heading}</strong></div>
            <div css={notificationTextStyle}>
              {item.text}
            </div>
          </div>

          {item.button.href ? (
            <ButtonLinkComponent href={item.button.href}>
              {item.button.text}
            </ButtonLinkComponent>
          ) : (
            <ButtonComponent onClick={item.button.onClick} text={item.button.text} />
          )}
        </div>
      ),
      onClose: () => dispatch(limitNotificationsRemoveItem(item.key)),
    };
  }), [dispatch, notifications, theme.alertColors.infoPrimary, theme.alertColors.infoTertiary, theme.alertColors.warningSecondary, theme.borderColors.error, theme.textColors.link, theme.textColors.warning]);
};
